/**
 * Publik portal modullari — sarlavhadagi menyu va bosh sahifadagi «tezkor
 * xizmatlar» qatori shu yagona ro'yxatdan quriladi.
 *
 * `MODULE_STATUS` — qaysi modul ishga tushgani. «soon» bo'lganlar menyuda va
 * tezkor qatorda «Tez kunda» belgisi bilan ko'rinadi (sahifasi ochiladi, lekin
 * u yerda modul haqida qisqa ma'lumot va tegishli ishlab turgan havolalar
 * turadi). Modul ishga tushganda faqat shu jadvaldagi qiymatni o'chirish yoki
 * «live» ga o'zgartirish kifoya.
 */
import type { HomeSpot } from "@/shared/lib/homeScroll";
import { HOME_NEWS_HREF, SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import type { IconName } from "@/shared/ui/Icon";

export type ModuleStatus = "live" | "soon";

export const MODULE_STATUS: Record<string, ModuleStatus> = {
  seedlings: "soon",
};

export function moduleStatus(key: string): ModuleStatus {
  return MODULE_STATUS[key] ?? "live";
}

export interface PublicNavEntry {
  key: string;
  /** Yo'l; «Yangiliklar» uchun — bosh sahifadagi blok (`/#yangiliklar`). */
  to: string;
  icon: IconName;
  /** i18n kaliti. */
  label: string;
  status: ModuleStatus;
  /** Bosh sahifa ichida silliq o'tadigan band: yuqoriga yoki yangiliklar blokiga. */
  home?: "top" | "news";
}

function entry(
  key: string,
  to: string,
  icon: IconName,
  label: string,
  extra: Partial<PublicNavEntry> = {},
): PublicNavEntry {
  return { key, to, icon, label, status: moduleStatus(key), ...extra };
}

/** Asosiy bo'limlar. */
export const PRIMARY_NAV: PublicNavEntry[] = [
  entry("home", "/", "home", "nav.home", { home: "top" }),
  entry("news", HOME_NEWS_HREF, "newspaper", "nav.news", { home: "news" }),
  entry("lease", SECTION_PATHS.lease, "polygon", "nav.lease"),
  entry("services", SECTION_PATHS.services, "briefcase", "nav.services"),
  entry("appeal", SECTION_PATHS.appeal, "send", "nav.appealSend"),
  entry("map", SECTION_PATHS.map, "map", "nav.map"),
  entry("market", SECTION_PATHS.market, "store", "nav.market"),
  entry("seedlings", SECTION_PATHS.seedlings, "leaf", "nav.seedlings"),
];

/** Ma'lumot bo'limlari. */
export const INFO_NAV: PublicNavEntry[] = [
  entry("about", SECTION_PATHS.about, "info", "nav.about"),
  entry("mobile-app", SECTION_PATHS["mobile-app"], "smartphone", "nav.app"),
  entry("contact", SECTION_PATHS.contact, "phone", "nav.contacts"),
];

/** Bosh sahifadagi tezkor xizmatlar qatori (ikonkali, ixcham). */
export const QUICK_SERVICES: Array<PublicNavEntry & { text: string }> = [
  { ...PRIMARY_NAV.find((item) => item.key === "lease")!, text: "quick.lease" },
  { ...PRIMARY_NAV.find((item) => item.key === "services")!, text: "quick.services" },
  { ...PRIMARY_NAV.find((item) => item.key === "appeal")!, text: "quick.appeal" },
  { ...PRIMARY_NAV.find((item) => item.key === "seedlings")!, text: "quick.seedlings" },
];

/**
 * Sarlavhadagi menyuda to'g'ridan-to'g'ri ko'rinadigan bandlar (shu tartibda); qolganlari
 * `PRIMARY_NAV` → `INFO_NAV` tartibida «Yana» ro'yxatida.
 */
export const HEADER_VISIBLE = ["home", "map", "lease", "market"];

/** Menyu bandi hozirgi sahifaga mosmi (bosh sahifada — qaysi qism ko'rinib turgani bo'yicha). */
export function isEntryActive(entry: PublicNavEntry, pathname: string, homeSpot: HomeSpot): boolean {
  const onHome = pathname === "/";
  if (entry.home === "top") return onHome && homeSpot === "top";
  if (entry.home === "news") return (onHome && homeSpot === "news") || pathname === SECTION_PATHS.news;
  /* Murojaatni kuzatish — murojaat sahifasining bir qismi. */
  if (entry.key === "appeal" && (pathname === SECTION_PATHS.tracking || pathname === "/track")) return true;
  return pathname === entry.to || pathname.startsWith(`${entry.to}/`);
}
